/**
 * The one confirmation support may have asked of a job's workspace (ADR-0088;
 * support-window.ts). The workspace selects the document, then runs its own
 * confirmation once that selection is on screen.
 *
 * A request never waits for ever. It used to stay pending when its selection
 * missed (another document came up instead), which refused every later support
 * confirmation, and then confirmed the document much later when the operator
 * happened to open it: ground truth nobody asked for at that moment. Now it
 * ends when the selection on screen is another document, after a time limit,
 * or when the workspace closes.
 */

export const SUPPORT_CONFIRM_OPEN_MILLISECONDS = 30_000;

export type SupportConfirmation = {
  documentId: string;
  settle: (outcome: unknown) => void;
  fail: (reason: Error) => void;
};

type Timers = {
  setTimeout: (callback: () => void, milliseconds: number) => unknown;
  clearTimeout: (handle: unknown) => void;
};

export class PendingSupportConfirmation {
  private pending: (SupportConfirmation & { timer: unknown }) | null = null;
  private readonly timers: Timers;
  private readonly limitMilliseconds: number;

  constructor(timers: Timers, limitMilliseconds = SUPPORT_CONFIRM_OPEN_MILLISECONDS) {
    this.timers = timers;
    this.limitMilliseconds = limitMilliseconds;
  }

  get busy() {
    return this.pending !== null;
  }

  /** Holds one request until its document is on screen, or the time limit ends it. */
  begin(request: SupportConfirmation) {
    if (this.pending) throw new Error('Another confirmation asked by support is still running.');
    const entry = { ...request, timer: undefined as unknown };
    entry.timer = this.timers.setTimeout(() => {
      if (this.pending !== entry) return;
      this.pending = null;
      entry.fail(new Error('The document did not open in time to be confirmed. Try again.'));
    }, this.limitMilliseconds);
    this.pending = entry;
  }

  /**
   * The committed selection, once the workspace can confirm: the request to run
   * when it is the requested document, or null. A different document on screen
   * ends the request, so it can never confirm later.
   */
  take(selectedDocumentId: string | undefined): SupportConfirmation | null {
    const entry = this.pending;
    if (!entry) return null;
    this.pending = null;
    this.timers.clearTimeout(entry.timer);
    if (selectedDocumentId !== entry.documentId) {
      entry.fail(
        new Error('Another document opened before this one could be confirmed. Try again.'),
      );
      return null;
    }
    return entry;
  }

  /** The workspace closed: the request fails rather than waiting for a document that will not open here. */
  cancel(reason = 'The job closed before the document was confirmed.') {
    const entry = this.pending;
    if (!entry) return;
    this.pending = null;
    this.timers.clearTimeout(entry.timer);
    entry.fail(new Error(reason));
  }
}
